import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  SecurityContext,
  signal,
  viewChild,
  DOCUMENT
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateDirective, TranslatePipe as NgxTranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  Breakpoint,
  BreakpointState,
  ConfigurationService,
  IfFeatureService,
  Level,
  LinkRegistryService,
  Logger,
  MediaQueryService,
  RouterBaseService,
  WINDOW_TOKEN
} from '@tdwb/core';
import { PageHeaderComponent, PageHeaderTitleComponent } from '@tdwb/webbroker/core';
import { IntegrationErrorComponent } from './error/integration-error.component';
import { IntegrationService } from './integration.service';
export interface IntegrationPageConfig {
  linkId: string;
  height: number;
  width: number | string;
  fixed: boolean;
  title?: string;
  url?: string;
  pageHeader?: string;
  backgroundColor?: boolean;
  baseDomainConfigKey?: string; // Specifies configuration call property to get domain
  baseDomain?: string; // The actual allowed domain value
  featureFlagForFullWidth?: string; // featureFlags for iframe with 100% width
}
@Component({
  selector: 'td-wb-integration',
  templateUrl: 'integration.component.html',
  styleUrls: ['integration.component.scss'],
  imports: [
    IntegrationErrorComponent,
    PageHeaderComponent,
    PageHeaderTitleComponent,
    TranslateDirective,
    NgxTranslatePipe
  ],
  host: {
    '(window:message)': 'onMessage($event)'
  }
})
export class IntegrationComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly LOGGER: Logger = Logger.getLogger('IntegrationComponent').setLevel(Level.ERROR);
  private static readonly INTEGRATION_PAGE_MAP = new Map<string, IntegrationPageConfig>([
    ['page.new-issues', {
      linkId: 'link_new_issues_legacy',
      height: 800,
      width: 962,
      fixed: true,
      pageHeader: 'newIssues',
      featureFlagForFullWidth: 'nipAuthenticationSSO'
    }],
    ['page.fixed-income', {
      linkId: 'link_fixed_income_b2b',
      height: 800,
      width: 962,
      fixed: true,
      pageHeader: 'fixedIncome'
    }],
    ['page.research', {
      linkId: 'link_b2b_transfer',
      height: 2525,
      width: undefined,
      fixed: false,
      backgroundColor: true,
      baseDomainConfigKey: 'modBaseDomain'
    }]
  ]);

  protected readonly iframe = viewChild<ElementRef<HTMLIFrameElement>>('iframe');
  protected readonly config = signal<IntegrationPageConfig | undefined>(undefined);
  protected readonly iframeSource = signal<SafeResourceUrl | null>(null);
  protected readonly contentReady = signal(false);
  protected readonly errorState = signal(false);
  protected readonly iframeWidth = signal<number | string>('100%');
  private loadInterval: ReturnType<typeof setInterval> | undefined;
  private resizeSubscription: Subscription | undefined;
  private afterViewInit = false;
  private mediaQuerySubscription: Subscription | undefined;
  private readonly browserWindow: Window = inject(WINDOW_TOKEN);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  private readonly linkRegistryService = inject(LinkRegistryService);
  private readonly routerService = inject(RouterBaseService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly mediaQueryService = inject(MediaQueryService);
  private readonly configurationService = inject(ConfigurationService);
  private readonly integrationService = inject(IntegrationService);
  private readonly ifFeatureService = inject(IfFeatureService);
  /// Listener for messages sent from 3rd party clients embedded on iFrame via postMessage
  onMessage(event: MessageEvent): void {
    const currentConfig = this.config();
    const message = event.data;
    if (message?.source === 'NIP') {
      if (message.type === 'KEEPALIVE') {
        this.integrationService.sendKeepAlive();
      }
    } else {
      if (currentConfig?.baseDomain === undefined || event.origin !== currentConfig.baseDomain) {
        IntegrationComponent.LOGGER.error('IntegrationComponent: Message source is empty or not from known domain');
        return;
      }
      if (message?.source === 'MOD') {
        const data: string = message?.data;
        IntegrationComponent.LOGGER.info('IntegrationComponent: MOD message data is ', data);
        switch (message.type) {
          case 'OPEN':
            if (data.match(/\/?#\//)) {
              this.browserWindow.location.replace(data);
            }
            return;
          case 'RESIZE':
            if (data.match(/^\d+(\.\d+)?(px|em|rem|vh|vw|%)$/)) {
              const iframe = this.iframe();
              if (iframe !== undefined) {
                this.renderer.setStyle(iframe.nativeElement, 'height', data);
              }
            }
            return;
          case 'SCROLLTOP':
            this.browserWindow.scrollTo(0, 0);
            return;
        }
      }
    }
  }

  ngOnInit(): void {
    const config = this.getPageConfig();
    this.config.set(config);
    this.iframeWidth.set(config.width ?? '100%');
    const iframeSource = this.sanitizer.sanitize(SecurityContext.URL, config.url);
    this.iframeSource.set(this.sanitizer.bypassSecurityTrustResourceUrl(iframeSource ?? ''));
    if (config.backgroundColor) {
      this.addMODStyle();
    }
    if (config.width === undefined) {
      this.mediaQuerySubscription = this.mediaQueryService.observe(Breakpoint.SM, Breakpoint.MD)
        .subscribe((state: BreakpointState): void => {
          this.iframeWidth.set(state.matches ? 1024 : '100%');
        });
    }
  }

  ngAfterViewInit(): void {
    this.afterViewInit = true;
  }

  ngOnDestroy(): void {
    if (this.loadInterval !== undefined) {
      clearInterval(this.loadInterval);
    }
    this.resizeSubscription?.unsubscribe();
    if (this.config()?.backgroundColor) {
      this.removeMODStyle();
    }
    this.mediaQuerySubscription?.unsubscribe();
  }

  onIframeLoad(): void {
    const config = this.config();
    if (this.afterViewInit && config?.fixed) {
      this.contentReady.set(true);
    }
  }

  private getPageConfig(): IntegrationPageConfig {
    const state = this.routerService.getCurrentStateName();
    const stateParams = this.routerService.getStateParams();
    const pageConfiguration = { ...IntegrationComponent.INTEGRATION_PAGE_MAP.get(state) };
    pageConfiguration.title = state;
    pageConfiguration.url = this.linkRegistryService.getLink(pageConfiguration.linkId, stateParams);
    if (pageConfiguration.baseDomainConfigKey !== undefined) {
      const baseDomain: string = this.configurationService.getProperty(pageConfiguration.baseDomainConfigKey, '');
      pageConfiguration.baseDomain = baseDomain.endsWith('/') ? baseDomain.slice(0, -1) : baseDomain;
    }
    if (
      pageConfiguration.featureFlagForFullWidth !== undefined &&
      this.ifFeatureService.getFeatureFlag(pageConfiguration.featureFlagForFullWidth) === true
    ) {
      pageConfiguration.width = '100%';
    }
    return pageConfiguration;
  }

  private addMODStyle(): void {
    this.renderer.addClass(this.document.body, 'td-wb-main--mod');
  }

  private removeMODStyle(): void {
    this.renderer.removeClass(this.document.body, 'td-wb-main--mod');
  }
}
